import _ from 'underscore';

import type { View } from '@trello/backbone';
// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';

import { trelloClipboard } from 'app/scripts/lib/trello-clipboard';
import { triggerList } from 'app/scripts/lib/util/event/trigger-list';
import { getPasted } from 'app/scripts/views/internal/data-transfer/Normalize';
import { Layout } from 'app/scripts/views/lib/Layout';

interface HandlerOptions {
  scope: string;
  precedence?: number;
}

interface PastedObject {
  [key: string]: string | File[] | undefined;
}

class PasteInput {
  public precedence: {
    [key: string]: number;
  };

  // Making this public so we can assert in unit tests that handlers are
  // getting added and removed correctly.
  public _handlers: {
    [key: string]: {
      target?: View;
      scope?: string;
      precedence?: number;
    };
  };

  constructor() {
    this.paste = this.paste.bind(this);
    this.precedence = {
      list: 4,
      card: 5,
      dialog: 10,
      default: 99,
    };
    this._handlers = {};

    $(document).on('paste', this.paste);

    // @ts-expect-error
    trelloClipboard.on('pasteImage', (image: File) => {
      return this.triggerPasted('files', [image]);
    });
  }

  addHandler(target: View, { scope, precedence }: HandlerOptions) {
    if (!precedence) {
      precedence = this.precedence[scope] ?? this.precedence.default;
    }

    if (!this._handlers[scope]) {
      this._handlers[scope] = {};
    }

    this._handlers[scope] = {
      target,
      scope,
      precedence,
    };
  }

  removeHandler(target: View) {
    return (() => {
      const result = [];
      for (const scope in this._handlers) {
        const { target: hTarget } = this._handlers[scope];
        if (target === hTarget) {
          result.push(delete this._handlers[scope]);
        }
      }
      return result;
    })();
  }

  _sortedHandlers() {
    return _.sortBy(_.values(this._handlers), (h) => -(h.precedence ?? 0));
  }

  paste(e: JQuery.TriggeredEvent) {
    getPasted(e.originalEvent).then((pasted: PastedObject) => {
      if (_.isEmpty(pasted)) {
        return;
      }

      // If they're pasting text into an input of some kind, just let the
      // default handling happen.
      const isPasteInInput = ['INPUT', 'TEXTAREA'].includes(e.target.tagName);

      // The target element for the paste event in Editor is not an input
      // and it gets removed from the DOM before this handler is called.
      // So we need to check if we're in edit mode, and of so, just allow
      // the default handling occur.
      const isPasteInEditor =
        document.activeElement?.closest('[contenteditable]') &&
        Layout.isEditing();

      if ((pasted.text || pasted.url) && (isPasteInInput || isPasteInEditor)) {
        // Unless it's the trello clipboard trying to intercept the paste,
        // in which case it's okay to handle it.
        if (!trelloClipboard.isClipboard(e.target)) {
          return;
        }
      }

      // Some applications (like PowerPoint and OneNote) will put several
      // different types on the clipboard when you copy. If there is some text,
      // assume they want that (and not a picture of the text).
      for (const type of ['url', 'text', 'files']) {
        if (pasted[type]) {
          this.triggerPasted(type, pasted[type]);
          break;
        }
      }
    });
  }

  triggerPasted(type: string, detail: string | File[] | undefined) {
    const elements = _.compact(
      this._sortedHandlers().map((handler) => handler.target?.el),
    );
    return triggerList(elements, `pasteinput:${type}`, { detail });
  }
}

// eslint-disable-next-line @trello/no-module-logic
const pasteInput = new PasteInput();
export { pasteInput as PasteInput };
